import React, { useEffect, useState } from "react";
import { X, DownloadSimple, Share } from "@phosphor-icons/react";
import { useI18n } from "@/context/I18nContext";

const DISMISS_KEY = "tf_pwa_dismissed_at";
const DISMISS_DAYS = 14;

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function recentlyDismissed() {
  const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
  if (!ts) return false;
  const ageDays = (Date.now() - ts) / (1000 * 60 * 60 * 24);
  return ageDays < DISMISS_DAYS;
}

/**
 * PWAInstallPrompt
 * - Android/desktop Chrome: listens for `beforeinstallprompt` and shows a banner
 *   with a real Install button that triggers the native prompt.
 * - iOS Safari: shows a hint with the Share → Add to Home Screen instructions
 *   (iOS has no programmatic install API).
 * - Hidden when already running as an installed app or recently dismissed.
 */
export default function PWAInstallPrompt() {
  const { t } = useI18n();
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);
  const [ios, setIos] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (recentlyDismissed()) return;

    const onBip = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", onBip);

    const onInstalled = () => {
      setVisible(false);
      setDeferred(null);
      localStorage.removeItem(DISMISS_KEY);
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS fallback — no event fires, so show the hint after a small delay
    if (isIOS()) {
      setIos(true);
      const tmr = setTimeout(() => setVisible(true), 4000);
      return () => {
        clearTimeout(tmr);
        window.removeEventListener("beforeinstallprompt", onBip);
        window.removeEventListener("appinstalled", onInstalled);
      };
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch {
      /* user dismissed */
    }
    setDeferred(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="pwa-install-prompt"
      className="fixed inset-x-3 z-50 md:left-auto md:right-6 md:max-w-sm md:w-full"
      style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 84px)" }}
      role="dialog"
      aria-live="polite"
    >
      <div
        className="rounded-2xl border shadow-2xl backdrop-blur-md p-4 flex gap-3 items-start"
        style={{
          background: "color-mix(in oklab, var(--tf-surface) 92%, transparent)",
          borderColor: "var(--tf-border)",
        }}
      >
        <div
          className="h-10 w-10 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: "var(--tf-surface-2)", border: "1px solid var(--tf-border)" }}
          aria-hidden="true"
        >
          {ios ? (
            <Share size={20} weight="bold" />
          ) : (
            <DownloadSimple size={20} weight="bold" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold leading-tight" style={{ color: "var(--tf-text)" }}>
            {t("pwa.install_title")}
          </div>
          <div className="text-xs mt-1 leading-snug" style={{ color: "var(--tf-text-muted)" }}>
            {ios ? t("pwa.ios_hint") : t("pwa.install_body")}
          </div>
          {!ios && (
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                data-testid="pwa-install-btn"
                onClick={install}
                className="px-3 py-1.5 rounded-full text-xs font-semibold transition active:scale-95 text-white"
                style={{ background: "var(--tf-blue)" }}
              >
                {t("pwa.install_cta")}
              </button>
              <button
                type="button"
                data-testid="pwa-install-dismiss"
                onClick={dismiss}
                className="px-3 py-1.5 rounded-full text-xs font-medium border transition active:scale-95"
                style={{ borderColor: "var(--tf-border)", color: "var(--tf-text-muted)" }}
              >
                {t("pwa.install_later")}
              </button>
            </div>
          )}
        </div>
        <button
          type="button"
          data-testid="pwa-install-close"
          onClick={dismiss}
          aria-label="Close"
          className="shrink-0 p-1 rounded-md transition hover:opacity-80"
          style={{ color: "var(--tf-text-muted)" }}
        >
          <X size={16} weight="bold" />
        </button>
      </div>
    </div>
  );
}
